import { useTranslation } from 'next-i18next';
import { PlusIcon } from '@heroicons/react/24/outline';

// Same JP card `/cerco-lavoro` uses today — lives in worker-app's legacy folder
// until a ui-ds-base JobPositionCard equivalent exists.
import { WorkerJpCard } from '../legacy';
import { Spinner, Tooltip } from '@restworld/ui-ds-base';
import { JobSimilarPositionsType } from '@restworld/utils-common';

interface SimilarJobsListProps {
  items: JobSimilarPositionsType[];
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  onItemClick?: (item: JobSimilarPositionsType, indexInList: number) => void;
}

// Vertical list of similar JPs rendered with the same `WorkerJpCard`
// used in the `/cerco-lavoro` explorer sidebar (via `ExplorerCardView`) —
// keeps the look-and-feel consistent across surfaces and avoids a
// parallel card design.
//
// "Mostra altre" pager appends the next page; once the cascade is
// exhausted (`hasMore=false`) the button becomes disabled with a tooltip.
export const SimilarJobsList = ({ items, hasMore, isLoading, onLoadMore, onItemClick }: SimilarJobsListProps) => {
  const { t } = useTranslation();

  if (items.length === 0) return null;

  return (
    <div className="flex flex-col gap-6 w-full">
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full list-none p-0 m-0">
        {items.map((item, idx) => (
          <li key={item.id ?? `${item.jobTitle}-${idx}`} className="w-full">
            <WorkerJpCard
              restaurantLogo={item.restaurantLogo ?? ''}
              restaurantName={item.restaurantName}
              publishedAt={typeof item.publishedAt === 'string' ? item.publishedAt : item.publishedAt?.toISOString()}
              city={item.city}
              redirectUrl={item.redirectUrl}
              jobTitle={item.jobTitle}
              occupation={item.occupation ?? ''}
              salaryMin={item.salaryMin ?? ''}
              salaryMax={item.salaryMax ?? ''}
              salary={item.salary}
              jobType={item.jobType ?? ''}
              onClick={() => onItemClick?.(item, idx)}
              wrapperStyle="w-full h-full"
            />
          </li>
        ))}
      </ul>
      <div className="flex justify-center pt-2">
        {/* No "ghost"/"link" variant exists in `ui-ds-base` Button (closest is */}
        {/* `tertiary`, which still draws an inset border). Using a plain      */}
        {/* button to de-emphasize this CTA.                                   */}
        {hasMore ? (
          <button
            type="button"
            onClick={onLoadMore}
            disabled={isLoading}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-700 bg-transparent border-0 p-0 cursor-pointer hover:underline disabled:text-neutral-200 disabled:cursor-not-allowed disabled:no-underline"
          >
            {isLoading ? <Spinner color="primary" size="sm" /> : <PlusIcon className="h-4 w-4" />}
            {/* Wrapped, not bare. A bare text node here is what produced the `insertBefore ...
                not a child of this node` crashes on this page: browser translation replaces
                text nodes, so the node React kept a reference to is no longer a child of the
                button, and the moment `isLoading` flips it calls insertBefore against it and
                throws. Inside a <span> the translator rewrites the span's contents and React's
                reference to the element stays valid. Symbolicated from the production component
                stack on 22/09/2026, which bottomed out at this button. */}
            <span>{t('job-position-page:offline_modal_show_more')}</span>
          </button>
        ) : (
          <Tooltip content={t('job-position-page:offline_modal_no_more_similar')}>
            <button
              type="button"
              disabled
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-700 bg-transparent border-0 p-0 cursor-pointer hover:underline disabled:text-neutral-200 disabled:cursor-not-allowed disabled:no-underline"
            >
              <PlusIcon className="h-4 w-4" />
              {/* Same wrap as the enabled branch above. Nothing swaps beside it today, but the
                  Tooltip re-renders around it, and leaving one of the two bare invites the same
                  crash back the next time this markup is edited. */}
              <span>{t('job-position-page:offline_modal_show_more')}</span>
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};
